import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./PullToRefresh.module.scss";
import Loader from "@src/components/UIElements/Loader";

import {
  closePullToRefreshAction,
  openPullToRefreshAction,
  triggerPullToRefreshAction,
  unTriggerPullToRefreshAction,
} from "./store";
import { getAwsAssets } from "../../utils";

const PullToRefresh = ({ layoutContentNode }) => {
  const resistance = 18;
  const maxPullDownDistance = 90;

  // REDUX DISPATCH
  const dispatch = useDispatch();

  // FETCHING STATES FROM REDUCER
  const bottomSheetReducer = useSelector((state) => state?.bottomSheetReducer);
  const isPullToRefreshOpen = useSelector(
    (state) => state?.pullToRefreshReducer?.isPullToRefreshOpen
  );
  const isPullToRefreshTriggered = useSelector(
    (state) => state?.pullToRefreshReducer?.isPullToRefreshTriggered
  );

  const loadInit = () => {
    refreshIconContainerRef?.current?.classList?.add("loading");
    loadingRef.current = true;
  };

  React.useEffect(() => {
    if (isPullToRefreshOpen) {
      removePullToRefreshLoader();
      if (refreshIconContainerRef.current?.style) {
        refreshIconContainerRef.current.style.display = "none";
      }
      dispatch(unTriggerPullToRefreshAction());
      loadingRef.current = false;
      refreshIconContainerRef.current?.classList?.remove("loading");
    }
  }, [isPullToRefreshOpen]);

  React.useEffect(() => {
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    document.addEventListener("touchmove", handleSwipe);
    document.addEventListener("touchend", handleTouchEnd);
    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleSwipe);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  // FUNCTION TO REMOVE PULL TO REFRESH LOADER
  const removePullToRefreshLoader = () => {
    setPullToRefreshContainerOpacity(0);
    setPullToRefreshContainerMarginTop(`-40px`);
    isPullToRefreshOpen && dispatch(closePullToRefreshAction());
    rotateRefreshIcon(0);
    if (layoutContentNode?.current?.style) {
      layoutContentNode.current.style.overflowY = "scroll";
    }
  };

  // REFS
  const loadingRef = React.useRef(false);
  const refreshIconRef = React.useRef();
  const refreshIconContainerRef = React.useRef();

  // TOUCH POINT REFS
  const yStart = React.useRef(0);
  const yEnd = React.useRef(0);

  // FUNCTION TO ROTATE REFRESH ICON
  const rotateRefreshIcon = (yChange) => {
    if (refreshIconRef?.current?.style) {
      refreshIconRef.current.style.transform = `scaleX(-1) rotate(-${yChange * 6
        }deg)`;
    }
  };

  // FUNCTION TO SET MARGIN TOP FOR PULL TO REFRESH ICON CONTAINER
  const setPullToRefreshContainerMarginTop = (marginTop) => {
    if (refreshIconContainerRef?.current?.style) {
      refreshIconContainerRef.current.style.marginTop = marginTop;
    }
  };

  // FUNCTION TO SET OPACITY FOR PULL TO REFRESH ICON CONTAINER
  const setPullToRefreshContainerOpacity = (yChange) => {
    if (refreshIconContainerRef?.current?.style) {
      refreshIconContainerRef.current.style.opacity = `${yChange / 260}`;
    }
  };

  // FUNCTION TO TRIGGER WHEN USER SWIPE STARTS
  const handleTouchStart = (e) => {
    if (refreshIconContainerRef.current?.style) {
      refreshIconContainerRef.current.style.display = "flex";
    }
    if (isPullToRefreshRunning()) return;
    if (ifPageScrolled()) return;
    if (isBottomSheetOpen()) return;

    const targetTouchPoint = e.targetTouches[0];
    if (!targetTouchPoint) return;
    yStart.current = targetTouchPoint.screenY;
  };

  // FUNCTION TO TRIGGER WHEN USER SWIPE ENDS
  const handleTouchEnd = (e) => {
    const targetTouchPoint = e.changedTouches[0];
    yEnd.current = targetTouchPoint.screenY;
    if (isPullToRefreshRunning() && refreshIconContainerRef?.current) {
      setPullToRefreshContainerMarginTop("70px");
      dispatch(triggerPullToRefreshAction());
    } else {
      removePullToRefreshLoader();
    }
  };

  // FUNCTION TO TRIGGER WHEN USER IS SWIPING
  const handleSwipe = (e) => {
    if (isPullToRefreshRunning()) return;
    if (ifPageScrolled()) return;
    if (isBottomSheetOpen()) return;

    const targetTouchPoint = e.changedTouches[0];
    yEnd.current = targetTouchPoint.screenY;

    const yChange =
      yStart.current < yEnd.current
        ? Math.abs(yStart.current - yEnd.current)
        : 0;

    if (yChange <= 0) return;

    if (layoutContentNode?.current?.style) {
      layoutContentNode.current.style.overflowY = "hidden";
    }

    const yDistanceMoved = Math.min(
      (yEnd.current - yStart.current) / resistance,
      maxPullDownDistance
    );

    if (
      yChange < 260 &&
      yChange > 0 &&
      yDistanceMoved > 6 &&
      refreshIconRef?.current
    ) {
      setPullToRefreshContainerOpacity(yChange);
      rotateRefreshIcon(yChange);

      if (yChange <= 100) {
        setPullToRefreshContainerMarginTop(`${yChange / 28}px`);
      }
      if (yChange >= 110) {
        setPullToRefreshContainerMarginTop(`${yChange / 30}px`);
      }
      if (yChange >= 120) {
        setPullToRefreshContainerMarginTop(`${yChange / 24}px`);
      }
      if (yChange >= 130) {
        setPullToRefreshContainerMarginTop(`${yChange / 20}px`);
      }
      if (yChange >= 140) {
        setPullToRefreshContainerMarginTop(`${yChange / 17}px`);
      }
      if (yChange >= 150) {
        setPullToRefreshContainerMarginTop(`${yChange / 15}px`);
      }
      if (yChange >= 160) {
        setPullToRefreshContainerMarginTop(`${yChange / 12}px`);
      }
      if (yChange >= 170) {
        setPullToRefreshContainerMarginTop(`${yChange / 10}px`);
      }
      if (yChange >= 180) {
        setPullToRefreshContainerMarginTop(`${yChange / 8.5}px`);
      }
      if (yChange >= 190) {
        setPullToRefreshContainerMarginTop(`${yChange / 6.2}px`);
      }
      if (yChange >= 200) {
        setPullToRefreshContainerMarginTop(`${yChange / 5.5}px`);
      }
      if (yChange >= 210) {
        setPullToRefreshContainerMarginTop(`${yChange / 4.6}px`);
      }
      if (yChange >= 220) {
        setPullToRefreshContainerMarginTop(`${yChange / 3.9}px`);
      }
      if (yChange >= 230) {
        setPullToRefreshContainerMarginTop(`${yChange / 2.9}px`);
      }
      if (yChange >= 240) {
        setPullToRefreshContainerMarginTop(`${yChange / 2.2}px`);
      }
      if (yChange >= 250) {
        setPullToRefreshContainerMarginTop(`${yChange / 2}px`);
      }
    } else if (yChange >= 260) return loadInit();
  };

  // FUNCTION TO CHECK IF PAGE IS SCROLLED
  const ifPageScrolled = () => {
    return layoutContentNode?.current?.scrollTop > 0;
  };

  // FUNCTION TO CHECK IF PULL TO REFRESH IS IN ACTION
  const isPullToRefreshRunning = () => {
    return loadingRef.current;
  };

  // FUNCTION TO CHECK IF BOTTOM SHEET IS OPEN
  const isBottomSheetOpen = () => {
    return Object.keys(bottomSheetReducer).some(
      (i) => bottomSheetReducer[i] && typeof bottomSheetReducer[i] !== "object"
    );
  };

  return (
    <div>
      <div className={`refreshIconContainer`} ref={refreshIconContainerRef}>
        <div style={{ zIndex: 500001 }}>
          {isPullToRefreshTriggered ? (
            <Loader colored small />
          ) : (
            <div>
              <img
                className="refreshIconContainer__icon"
                ref={refreshIconRef}
                src={getAwsAssets("pullToRefreshIcon.svg", "layout")}
                alt="pullToRefreshIcon"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PullToRefresh;
